// src/context/AuthContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { getIfConfig } from "../config/ifConfig";
import { normalizeReturnTo, ROUTES, withQuery } from "../routes/appRoutes";
import {
  clearProfileCompletionSession,
  hasRecentlyCompletedProfile,
} from "../routes/profileCompletionSession";
import {
  AUTH_INVALID_EVENT,
  AUTH_INVALID_REASONS,
} from "../services/authInvalidEvent";
import { apiRequest } from "../services/apiService";

const AuthContext = createContext(null);

const SESSION_HYDRATE_TIMEOUT_MS = 8000;

/**
 * Enterprise UX guard:
 * Do not revalidate auth every time the user briefly switches windows/tabs.
 *
 * Behaviour:
 * - Initial app load still validates immediately.
 * - Manual refreshAuth still works.
 * - Focus/return-to-app revalidation is silent and throttled.
 */
const FOCUS_REVALIDATE_MIN_MS = 5 * 60 * 1000;
const MIN_BACKGROUND_MS_FOR_REVALIDATE = 60 * 1000;

const AUTH_ROUTE_PATHS = new Set(
  [
    ROUTES.login,
    ROUTES.register,
    "/login",
    "/signin",
    "/sign-in",
    "/signup",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/auth/callback",
  ].filter(Boolean)
);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}

// Legacy compatibility for old apiService imports.
export async function getGlobalAccessToken() {
  return null;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function readCookie(name) {
  if (typeof document === "undefined" || !name) return null;

  const pattern = new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`);
  const match = document.cookie.match(pattern);

  return match ? decodeURIComponent(match[1]) : null;
}

function isAuthPathname(pathname) {
  const clean = String(pathname || "/").trim() || "/";

  if (AUTH_ROUTE_PATHS.has(clean)) {
    return true;
  }

  return clean.startsWith("/auth/");
}

function isCurrentAuthRoute() {
  if (typeof window === "undefined") return false;
  return isAuthPathname(window.location.pathname || "/");
}

function currentReturnTo(fallback = ROUTES.home) {
  if (typeof window === "undefined") {
    return fallback;
  }

  const pathname = window.location.pathname || "/";

  if (isAuthPathname(pathname)) {
    return fallback;
  }

  const value = `${pathname}${window.location.search || ""}${
    window.location.hash || ""
  }`;

  return normalizeReturnTo(value) || fallback;
}

function normalizeSessionUser(data) {
  return {
    id: data?.sub || data?.user_id || null,
    tenantId: data?.tenantId || data?.tenant_id || null,
    email: data?.email || "",
    username: data?.username || data?.email || "",
    groups: Array.isArray(data?.groups) ? data.groups : [],
    tier: data?.tier || null,
  };
}

function firstObject(...values) {
  return (
    values.find(
      (value) => value && typeof value === "object" && !Array.isArray(value)
    ) || null
  );
}

function normalizeMe(me) {
  if (!me || typeof me !== "object") return null;

  const identity =
    firstObject(me.identity, me.userIdentity, me.user_identity) || {};

  const profile =
    firstObject(me.profile, me.userProfile, me.user_profile) || null;

  const profileDetails =
    firstObject(
      me.profile_details,
      me.profileDetails,
      me.details,
      me.profile?.details,
      me.profile?.profile_details,
      me.profile?.profileDetails
    ) || null;

  const subscription =
    firstObject(me.subscription, me.billing, me.subscription_details) || null;

  const entitlements =
    firstObject(me.entitlements, me.permissions, me.plan_entitlements) || null;

  const access = firstObject(me.access, me.authAccess, me.auth_access) || null;

  const groups = Array.isArray(access?.groups)
    ? access.groups
    : Array.isArray(me.groups)
      ? me.groups
      : [];

  return {
    raw: me,

    identity,
    profile,
    profile_details: profileDetails,
    profileDetails,

    subscription,
    entitlements,
    access,
    groups,
    isPlatformAdmin: Boolean(access?.isPlatformAdmin),

    user_id:
      identity.user_id ??
      identity.userId ??
      profileDetails?.user_id ??
      profileDetails?.userId ??
      profile?.user_id ??
      profile?.userId ??
      null,

    tenant_id:
      identity.tenant_id ??
      identity.tenantId ??
      profileDetails?.tenant_id ??
      profileDetails?.tenantId ??
      profile?.tenant_id ??
      profile?.tenantId ??
      null,

    email: identity.email ?? profile?.email ?? profileDetails?.email ?? null,

    username:
      identity.username ??
      profile?.username ??
      profileDetails?.username ??
      identity.email ??
      profile?.email ??
      null,
  };
}

function normalizeHostedUiNext(next, fallback) {
  return normalizeReturnTo(next) || fallback;
}

function readBooleanCandidate(value) {
  if (typeof value === "boolean") return value;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  return null;
}

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function resolveProfileCompletion(meNorm) {
  if (!meNorm) {
    return {
      profileReady: false,
      profileCompleted: false,
      onboardingStatus: "unknown",
    };
  }

  const raw = meNorm.raw || {};
  const profile = meNorm.profile || {};
  const profileDetails = meNorm.profile_details || meNorm.profileDetails || {};
  const identity = meNorm.identity || {};

  const explicitCandidates = [
    raw?.onboarding?.profileCompleted,
    raw?.onboarding?.profile_completed,
    raw?.onboarding?.completed,
    raw?.profileCompleted,
    raw?.profile_completed,
    raw?.isProfileComplete,
    raw?.is_profile_complete,

    profile?.profileCompleted,
    profile?.profile_completed,
    profile?.isProfileComplete,
    profile?.is_profile_complete,
    profile?.completed,

    profileDetails?.profileCompleted,
    profileDetails?.profile_completed,
    profileDetails?.isProfileComplete,
    profileDetails?.is_profile_complete,
    profileDetails?.completed,

    identity?.profileCompleted,
    identity?.profile_completed,
  ];

  for (const candidate of explicitCandidates) {
    const parsed = readBooleanCandidate(candidate);

    if (parsed === true) {
      return {
        profileReady: true,
        profileCompleted: true,
        onboardingStatus: "complete",
      };
    }

    if (parsed === false) {
      return {
        profileReady: true,
        profileCompleted: false,
        onboardingStatus: "required",
      };
    }
  }

  const explicitStep =
    raw?.onboarding?.status ||
    raw?.onboarding?.step ||
    raw?.onboardingStatus ||
    raw?.onboarding_status ||
    profile?.onboardingStatus ||
    profile?.onboarding_status ||
    profileDetails?.onboardingStatus ||
    profileDetails?.onboarding_status ||
    null;

  if (typeof explicitStep === "string" && explicitStep.trim()) {
    const normalized = explicitStep.trim().toLowerCase();

    if (["complete", "completed", "done", "finished"].includes(normalized)) {
      return {
        profileReady: true,
        profileCompleted: true,
        onboardingStatus: "complete",
      };
    }

    if (
      [
        "required",
        "incomplete",
        "pending",
        "in_progress",
        "in-progress",
      ].includes(normalized)
    ) {
      return {
        profileReady: true,
        profileCompleted: false,
        onboardingStatus: "required",
      };
    }
  }

  const familyName =
    profileDetails?.family_name ??
    profileDetails?.familyName ??
    profile?.family_name ??
    profile?.familyName ??
    null;

  const birthdate =
    profileDetails?.birthdate ??
    profileDetails?.birthDate ??
    profileDetails?.date_of_birth ??
    profileDetails?.dateOfBirth ??
    profile?.birthdate ??
    profile?.birthDate ??
    profile?.date_of_birth ??
    profile?.dateOfBirth ??
    null;

  const accountType =
    profileDetails?.account_type ??
    profileDetails?.accountType ??
    profile?.account_type ??
    profile?.accountType ??
    null;

  if (hasText(familyName) && hasText(birthdate) && hasText(accountType)) {
    return {
      profileReady: true,
      profileCompleted: true,
      onboardingStatus: "complete",
    };
  }

  const businessName =
    profile?.business_name ??
    profile?.businessName ??
    profileDetails?.business_name ??
    profileDetails?.businessName ??
    null;

  const industry = profile?.industry ?? profileDetails?.industry ?? null;

  const location =
    profile?.location ??
    profile?.city ??
    profileDetails?.location ??
    profileDetails?.city ??
    null;

  const hasStrongProfileSignal = [businessName, industry, location].some(
    hasText
  );

  if (hasStrongProfileSignal) {
    return {
      profileReady: true,
      profileCompleted: true,
      onboardingStatus: "complete",
    };
  }

  return {
    profileReady: true,
    profileCompleted: false,
    onboardingStatus: "required",
  };
}

function applyProfileCompletionGrace(completion) {
  if (completion?.profileCompleted) {
    clearProfileCompletionSession();
    return completion;
  }

  if (hasRecentlyCompletedProfile()) {
    return {
      profileReady: true,
      profileCompleted: true,
      onboardingStatus: "complete",
    };
  }

  return completion;
}

async function fetchWithTimeout(
  url,
  options = {},
  timeoutMs = SESSION_HYDRATE_TIMEOUT_MS
) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function isAuthSessionPath(path) {
  const clean = String(path || "")
    .split("?")[0]
    .replace(/\/+$/, "")
    .toLowerCase();

  return clean === "/auth/session" || clean.endsWith("/auth/session");
}

async function responseLooksLikeCsrf(res) {
  try {
    const clone = res.clone();
    const contentType = clone.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const parsed = await clone.json();
      const error =
        parsed?.error && typeof parsed.error === "object"
          ? parsed.error
          : parsed || {};

      const code = String(error?.code || parsed?.code || "").toUpperCase();
      const reason = String(error?.reason || parsed?.reason || "").toUpperCase();
      const message = String(
        error?.message || parsed?.message || parsed?.detail || ""
      ).toLowerCase();

      return (
        code.includes("CSRF") ||
        reason.includes("CSRF") ||
        message.includes("csrf") ||
        message.includes("security check")
      );
    }

    const text = (await clone.text()).toLowerCase();

    return text.includes("csrf") || text.includes("security check");
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const { API_URL } = getIfConfig();

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [profileReady, setProfileReady] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState("unknown");

  const mountedRef = useRef(true);
  const userRef = useRef(null);
  const hydrateSeqRef = useRef(0);
  const redirectInFlightRef = useRef(false);
  const redirectTimerRef = useRef(null);

  /**
   * Focus/visibility revalidation guards.
   * These prevent unnecessary auth calls and UI flicker when user returns
   * from another browser tab/window/app.
   */
  const hiddenAtRef = useRef(null);
  const lastFocusRevalidateAtRef = useRef(0);
  const focusHydrateInFlightRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const safeSet = useCallback(
    (setter) => (value) => {
      if (mountedRef.current) {
        setter(value);
      }
    },
    []
  );

  const safeSetUser = useMemo(() => safeSet(setUser), [safeSet]);
  const safeSetHydrating = useMemo(() => safeSet(setHydrating), [safeSet]);
  const safeSetLoading = useMemo(() => safeSet(setLoading), [safeSet]);
  const safeSetError = useMemo(() => safeSet(setError), [safeSet]);
  const safeSetAuthReady = useMemo(() => safeSet(setAuthReady), [safeSet]);
  const safeSetProfileReady = useMemo(() => safeSet(setProfileReady), [safeSet]);
  const safeSetProfileCompleted = useMemo(
    () => safeSet(setProfileCompleted),
    [safeSet]
  );
  const safeSetOnboardingStatus = useMemo(
    () => safeSet(setOnboardingStatus),
    [safeSet]
  );

  const resetResolvedAuthState = useCallback(() => {
    clearProfileCompletionSession();
    safeSetUser(null);
    safeSetProfileReady(true);
    safeSetProfileCompleted(false);
    safeSetOnboardingStatus("anonymous");
  }, [
    safeSetUser,
    safeSetProfileReady,
    safeSetProfileCompleted,
    safeSetOnboardingStatus,
  ]);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/me`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (!res.ok) return null;

      const me = await res.json();
      return normalizeMe(me);
    } catch {
      return null;
    }
  }, [API_URL]);

  const hydrateSession = useCallback(
    async ({
      showSpinner = false,
      markAuthReady = false,
      silent = false,
    } = {}) => {
      const seq = ++hydrateSeqRef.current;

      safeSetError(null);

      if (showSpinner) {
        safeSetHydrating(true);
      }

      /**
       * Important:
       * For silent background/focus checks, do not reset profileReady to false.
       * Resetting this during focus revalidation causes the whole app to show
       * the boot/loading screen even though the user is already inside the app.
       */
      if (!silent) {
        if (hasRecentlyCompletedProfile()) {
          safeSetProfileReady(true);
          safeSetProfileCompleted(true);
          safeSetOnboardingStatus("complete");
        } else {
          safeSetProfileReady(false);
          safeSetOnboardingStatus("unknown");
        }
      }

      try {
        /**
         * /auth/session returning 401 means "anonymous visitor".
         * apiRequest converts that into null because of unauthenticatedAsNull.
         *
         * This avoids:
         * 401 -> AUTH_INVALID_EVENT -> /auth/logout -> /login -> 401 loop.
         */
        const session = await apiRequest("/auth/session", "GET", null, {
          requiresAuth: false,
          suppressAuthInvalidEvent: true,
          unauthenticatedAsNull: true,
          withCredentials: true,
          timeoutMs: SESSION_HYDRATE_TIMEOUT_MS,
        });

        if (!session) {
          if (seq === hydrateSeqRef.current) {
            resetResolvedAuthState();
          }

          return false;
        }

        const baseUser = normalizeSessionUser(session);
        const meNorm = await fetchMe();

        const mergedUser = {
          ...baseUser,

          email: meNorm?.email ?? baseUser.email,
          username: meNorm?.username ?? baseUser.username,
          tenantId: meNorm?.tenant_id ?? baseUser.tenantId,
          id: meNorm?.user_id ?? baseUser.id,

          identity: meNorm?.identity ?? null,
          profile: meNorm?.profile ?? null,
          profile_details: meNorm?.profile_details ?? null,
          profileDetails: meNorm?.profileDetails ?? null,
          subscription: meNorm?.subscription ?? null,
          entitlements: meNorm?.entitlements ?? null,

          me: meNorm?.raw ?? null,
          raw: meNorm?.raw ?? null,
        };

        const completion = applyProfileCompletionGrace(
          resolveProfileCompletion(meNorm)
        );

        if (seq === hydrateSeqRef.current) {
          safeSetUser(mergedUser);
          safeSetProfileCompleted(completion.profileCompleted);
          safeSetOnboardingStatus(completion.onboardingStatus);
          safeSetProfileReady(completion.profileReady);
        }

        return true;
      } catch {
        /**
         * Important:
         * A silent focus refresh can fail due to network sleep, laptop wake,
         * temporary API delay, or browser throttling.
         *
         * Do not destroy the current logged-in UI for a silent refresh failure.
         */
        if (seq === hydrateSeqRef.current && !silent) {
          resetResolvedAuthState();
        }

        return false;
      } finally {
        if (seq === hydrateSeqRef.current) {
          safeSetAuthReady(true);

          if (showSpinner) {
            safeSetHydrating(false);
          } else if (!silent) {
            safeSetHydrating(false);
          }
        }
      }
    },
    [
      fetchMe,
      resetResolvedAuthState,
      safeSetAuthReady,
      safeSetError,
      safeSetHydrating,
      safeSetOnboardingStatus,
      safeSetProfileCompleted,
      safeSetProfileReady,
      safeSetUser,
    ]
  );

  useEffect(() => {
    void hydrateSession({
      showSpinner: true,
      markAuthReady: true,
      silent: false,
    });
  }, [hydrateSession]);

  const beginRedirect = useCallback(() => {
    if (redirectInFlightRef.current) return false;

    redirectInFlightRef.current = true;
    safeSetError(null);
    safeSetLoading(true);

    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = window.setTimeout(() => {
      redirectInFlightRef.current = false;
      safeSetLoading(false);
      safeSetError("Redirect is taking longer than expected. Please try again.");
      redirectTimerRef.current = null;
    }, 20000);

    return true;
  }, [safeSetError, safeSetLoading]);

  const clearRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = null;
  }, []);

  useEffect(() => {
    const maybeRevalidateOnReturn = () => {
      if (redirectInFlightRef.current) return;

      /**
       * Avoid repeated anonymous /auth/session calls while sitting on /login.
       * This keeps the console clean and avoids unnecessary auth churn.
       */
      if (isCurrentAuthRoute() && !userRef.current) return;

      const now = Date.now();
      const hiddenAt = hiddenAtRef.current;
      const hiddenDurationMs = hiddenAt ? now - hiddenAt : null;

      const wasBriefWindowSwitch =
        hiddenDurationMs !== null &&
        hiddenDurationMs < MIN_BACKGROUND_MS_FOR_REVALIDATE;

      if (wasBriefWindowSwitch) return;

      const recentlyRevalidated =
        now - lastFocusRevalidateAtRef.current < FOCUS_REVALIDATE_MIN_MS;

      if (recentlyRevalidated) return;

      if (focusHydrateInFlightRef.current) return;

      lastFocusRevalidateAtRef.current = now;

      focusHydrateInFlightRef.current = hydrateSession({
        showSpinner: false,
        markAuthReady: false,
        silent: true,
      }).finally(() => {
        focusHydrateInFlightRef.current = null;
      });
    };

    const onFocus = () => {
      maybeRevalidateOnReturn();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        clearRedirectTimer();
        return;
      }

      if (document.visibilityState === "visible") {
        maybeRevalidateOnReturn();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [clearRedirectTimer, hydrateSession]);

  const startHostedUi = useCallback(
    ({ provider = "COGNITO", mode = "login", next = ROUTES.home } = {}) => {
      const ok = beginRedirect();
      if (!ok) return;

      const fallback = mode === "signup" ? ROUTES.completeProfile : ROUTES.home;
      const safeNext = normalizeHostedUiNext(next, fallback);

      const url =
        `${API_URL}/auth/start?` +
        `provider=${encodeURIComponent(provider)}` +
        `&mode=${encodeURIComponent(mode)}` +
        `&next=${encodeURIComponent(safeNext)}`;

      try {
        window.location.assign(url);
      } catch {
        redirectInFlightRef.current = false;
        clearRedirectTimer();

        safeSetLoading(false);
        safeSetError("Redirect failed. Please try again.");
      }
    },
    [API_URL, beginRedirect, clearRedirectTimer, safeSetError, safeSetLoading]
  );

  const socialLogin = useCallback(
    (provider = "COGNITO", next = ROUTES.home) =>
      startHostedUi({ provider, mode: "login", next }),
    [startHostedUi]
  );

  const socialSignup = useCallback(
    (provider = "COGNITO", next = ROUTES.completeProfile) =>
      startHostedUi({ provider, mode: "signup", next }),
    [startHostedUi]
  );

  const logoutLocal = useCallback(() => {
    redirectInFlightRef.current = false;
    safeSetError(null);
    safeSetLoading(false);
    safeSetAuthReady(true);
    resetResolvedAuthState();
    clearRedirectTimer();
  }, [
    clearRedirectTimer,
    resetResolvedAuthState,
    safeSetAuthReady,
    safeSetError,
    safeSetLoading,
  ]);

  const logout = useCallback(
    ({ next = ROUTES.login, origin } = {}) => {
      const ok = beginRedirect();
      if (!ok) return;

      resetResolvedAuthState();

      const resolvedOrigin = origin || window.location.origin;
      const safeNext = normalizeHostedUiNext(next, ROUTES.login);

      const url =
        `${API_URL}/auth/logout?` +
        `origin=${encodeURIComponent(resolvedOrigin)}` +
        `&next=${encodeURIComponent(safeNext)}`;

      try {
        window.location.assign(url);
      } catch {
        redirectInFlightRef.current = false;
        clearRedirectTimer();

        safeSetLoading(false);
        safeSetError("Logout redirect failed. Please try again.");
      }
    },
    [
      API_URL,
      beginRedirect,
      clearRedirectTimer,
      resetResolvedAuthState,
      safeSetError,
      safeSetLoading,
    ]
  );

  const forceRelogin = useCallback(
    ({ reason = AUTH_INVALID_REASONS.SESSION_EXPIRED, returnTo } = {}) => {
      if (redirectInFlightRef.current) return;

      /**
       * If already on an auth page and anonymous, do not bounce through
       * /auth/logout again. That is the loop we are killing.
       */
      if (isCurrentAuthRoute() && !userRef.current) {
        logoutLocal();
        return;
      }

      redirectInFlightRef.current = true;
      clearRedirectTimer();

      safeSetError(null);
      safeSetLoading(true);
      safeSetAuthReady(true);

      resetResolvedAuthState();

      const safeReturnTo =
        normalizeReturnTo(returnTo) || currentReturnTo(ROUTES.home);

      const loginNext = withQuery(ROUTES.login, {
        reason,
        returnTo: safeReturnTo,
      });

      const resolvedOrigin = window.location.origin;

      const logoutUrl =
        `${API_URL}/auth/logout?` +
        `origin=${encodeURIComponent(resolvedOrigin)}` +
        `&next=${encodeURIComponent(loginNext)}`;

      window.location.replace(logoutUrl);
    },
    [
      API_URL,
      clearRedirectTimer,
      logoutLocal,
      resetResolvedAuthState,
      safeSetAuthReady,
      safeSetError,
      safeSetLoading,
    ]
  );

  useEffect(() => {
    const onAuthInvalid = (event) => {
      const detail = event?.detail || {};

      forceRelogin({
        reason: detail.reason || AUTH_INVALID_REASONS.SESSION_EXPIRED,
        returnTo: detail.returnTo,
      });
    };

    window.addEventListener(AUTH_INVALID_EVENT, onAuthInvalid);

    return () => {
      window.removeEventListener(AUTH_INVALID_EVENT, onAuthInvalid);
    };
  }, [forceRelogin]);

  const apiFetch = useCallback(
    async (path, { method = "GET", headers = {}, body } = {}) => {
      const upper = String(method || "GET").toUpperCase();
      const isMutating = !["GET", "HEAD", "OPTIONS"].includes(upper);
      const csrf = isMutating ? readCookie("if_csrf") : null;

      if (isMutating && !csrf) {
        forceRelogin({
          reason: AUTH_INVALID_REASONS.CSRF_MISSING,
          returnTo: currentReturnTo(ROUTES.home),
        });

        throw new Error("Session expired. Please sign in again.");
      }

      const res = await fetchWithTimeout(`${API_URL}${path}`, {
        method: upper,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(body !== undefined && body !== null && isMutating
            ? { "Content-Type": "application/json" }
            : {}),
          ...(isMutating && csrf ? { "X-CSRF-Token": csrf } : {}),
          ...headers,
        },
        body:
          body !== undefined && body !== null && isMutating
            ? JSON.stringify(body)
            : undefined,
      });

      if (
        (res.status === 401 || res.status === 419) &&
        !isAuthSessionPath(path)
      ) {
        forceRelogin({
          reason:
            res.status === 419
              ? AUTH_INVALID_REASONS.CSRF_FAILED
              : AUTH_INVALID_REASONS.SESSION_EXPIRED,
          returnTo: currentReturnTo(ROUTES.home),
        });
      }

      /**
       * Do not treat every mutating 403 as CSRF.
       * 403 can also mean feature disabled, plan limit, or entitlement missing.
       */
      if (
        res.status === 403 &&
        isMutating &&
        (await responseLooksLikeCsrf(res))
      ) {
        forceRelogin({
          reason: AUTH_INVALID_REASONS.CSRF_FAILED,
          returnTo: currentReturnTo(ROUTES.home),
        });
      }

      return res;
    },
    [API_URL, forceRelogin]
  );

  const refreshAuth = useCallback(
    (options = {}) => {
      const showSpinner =
        typeof options === "boolean"
          ? options
          : Boolean(options?.showSpinner ?? options?.forceSpinner ?? false);

      return hydrateSession({
        showSpinner,
        markAuthReady: false,
        silent: false,
      });
    },
    [hydrateSession]
  );

  const value = useMemo(
    () => ({
      user,
      authReady,
      hydrating,
      loading,
      error,

      profileReady,
      profileCompleted,
      onboardingStatus,

      socialLogin,
      socialSignup,
      logout,
      logoutLocal,
      forceRelogin,

      refreshAuth,
      apiFetch,

      isAuthenticated: !!user,
    }),
    [
      user,
      authReady,
      hydrating,
      loading,
      error,
      profileReady,
      profileCompleted,
      onboardingStatus,
      socialLogin,
      socialSignup,
      logout,
      logoutLocal,
      forceRelogin,
      refreshAuth,
      apiFetch,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;